import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import InputField from '../../components/InputField';
import ValidationErrors from '../validationErrors';
import { TranslationHelpers } from '../../../../i18n/translationHelpers';

interface IPostalCodeFieldProps {
    autoFocus?: boolean;
    intl: InjectedIntl;
    value?: string;
    required?: boolean;
    disabled?: boolean;
}

class PostalCodeField extends React.Component<IPostalCodeFieldProps> {
    private readonly ID: string;
    private readonly LABEL_ID: string;
    private readonly ERROR_REQUIRED_ID: string;
    private readonly MAX_LENGTH: number;

    constructor(props: IPostalCodeFieldProps) {
        super(props);

        this.ID = 'postalCode';
        this.LABEL_ID = 'label.postal_code';
        this.ERROR_REQUIRED_ID = 'error.postal_code.required';
        this.MAX_LENGTH = 64;

        this.validate = this.validate.bind(this);
        this.renderInputField = this.renderInputField.bind(this);
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderInputField} />;
    }

    private validate(value: string): string {
        const { intl, required } = this.props;

        if (!value && required) {
            return intl.formatMessage(
                TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID),
            );
        }

        if (value.length > this.MAX_LENGTH) {
            return ValidationErrors.fieldTooLongMessage(intl, this.MAX_LENGTH);
        }

        if (!/^[\x00-\x7F]*$/.test(value)) {
            // Should only contain ASCII characters
            return ValidationErrors.fieldInvalidMessage(intl, this.LABEL_ID);
        }

        if (!/^[\s\-\d\w]*$/.test(value)) {
            // Should only contain ASCII characters
            return ValidationErrors.fieldInvalidMessage(intl, this.LABEL_ID);
        }

        return '';
    }

    private renderInputField({ field, form: { touched, errors } }: FieldProps): ReactNode {
        const { autoFocus, value, required, disabled = false } = this.props;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;

        return (
            <InputField
                autoComplete="postal-code"
                labelMessage={TranslationHelpers.getMessageDescriptor(this.LABEL_ID)}
                type="text"
                id={this.ID}
                maxLength={this.MAX_LENGTH}
                name={this.ID}
                value={value}
                error={errorMessage}
                autoFocus={autoFocus}
                required={required}
                disabled={disabled}
            />
        );
    }
}

export default injectIntl(PostalCodeField);
